import * as stylex from '@stylexjs/stylex';
import clsx from 'clsx';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { propertyStyles as styles } from '@/tools/itemProperties/itemProperties.styles';
import { focusAndSelectElement } from '@/utilities/dom.utilities';
import { TextArea } from '@seeqdev/qomponents';
import type { StyleXStyles } from '@stylexjs/stylex';

export type DescriptionClassNames = {
  container?: string;
  editableContainer?: string;
  editableLabel?: string;
  editableValue?: string;
  textArea?: string;
  readonlyContainer?: string;
  readonlyLabel?: string;
  readonlyValue?: string;
  readonlyText?: string;
};

export type DescriptionProps = {
  canWrite: () => boolean;
  originalDesc: string;
  updateDescription: (description: string) => Promise<void>;
  isWorkbookLocked: boolean;
  testId?: string;
  classNames?: DescriptionClassNames;
  xstyle?: StyleXStyles;
  editableContainerXstyle?: StyleXStyles;
  editableLabelXstyle?: StyleXStyles;
  editableValueXstyle?: StyleXStyles;
  textAreaXstyle?: StyleXStyles;
  readonlyContainerXstyle?: StyleXStyles;
  readonlyLabelXstyle?: StyleXStyles;
  readonlyValueXstyle?: StyleXStyles;
  readonlyTextXstyle?: StyleXStyles;
};

export const Description = ({
  canWrite,
  originalDesc,
  updateDescription,
  isWorkbookLocked,
  testId = 'Description',
  classNames,
  xstyle,
  editableContainerXstyle,
  editableLabelXstyle,
  editableValueXstyle,
  textAreaXstyle,
  readonlyContainerXstyle,
  readonlyLabelXstyle,
  readonlyValueXstyle,
  readonlyTextXstyle,
}: DescriptionProps) => {
  const { t } = useTranslation();

  const [description, setDescription] = useState(originalDesc);

  useEffect(() => {
    setDescription(originalDesc);
  }, [originalDesc]);

  return (
    <dl
      data-testid={testId}
      {...stylex.props(xstyle)}
      className={clsx(classNames?.container, stylex.props(xstyle).className)}>
      {canWrite() && (
        <div
          {...stylex.props(editableContainerXstyle)}
          className={clsx(
            'propList__item',
            classNames?.editableContainer,
            stylex.props(editableContainerXstyle).className,
          )}
          data-testid="descriptionContainer">
          <dt
            {...stylex.props(styles.descriptionLabel, editableLabelXstyle)}
            className={clsx(
              'propList__item__name',
              classNames?.editableLabel,
              stylex.props(styles.descriptionLabel, editableLabelXstyle).className,
            )}>
            {t('DESCRIPTION')}
          </dt>
          <dd
            {...stylex.props(editableValueXstyle)}
            className={clsx(
              'propList__item__value',
              classNames?.editableValue,
              stylex.props(editableValueXstyle).className,
            )}>
            <TextArea
              extraClassNames={classNames?.textArea}
              xstyle={[styles.descriptionInput, textAreaXstyle]}
              id="specDescription"
              testId="specDescription"
              value={description ?? ''}
              onChange={(e) => setDescription(e.target.value)}
              onFocus={focusAndSelectElement}
              onBlur={() => updateDescription(description)}
              rows={4}
              disabled={isWorkbookLocked}
            />
          </dd>
        </div>
      )}
      {!(_.isEmpty(description) || canWrite()) && (
        <div
          {...stylex.props(readonlyContainerXstyle)}
          className={clsx(
            'propList__item',
            classNames?.readonlyContainer,
            stylex.props(readonlyContainerXstyle).className,
          )}
          data-testid="descriptionDisplayContainer">
          <dt
            {...stylex.props(styles.descriptionLabel, readonlyLabelXstyle)}
            className={clsx(
              classNames?.readonlyLabel,
              stylex.props(styles.descriptionLabel, readonlyLabelXstyle).className,
            )}>
            {t('DESCRIPTION')}
          </dt>
          <dd
            {...stylex.props(readonlyValueXstyle)}
            className={clsx(
              'propList__item__value',
              classNames?.readonlyValue,
              stylex.props(readonlyValueXstyle).className,
            )}>
            <p
              {...stylex.props(styles.descriptionText, readonlyTextXstyle)}
              className={clsx(
                classNames?.readonlyText,
                stylex.props(styles.descriptionText, readonlyTextXstyle).className,
              )}
              data-testid="descriptionText">
              {description}
            </p>
          </dd>
        </div>
      )}
    </dl>
  );
};
